import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ONE, ZERO } from '@/constants';
import { useCurrentUser, useSurvey, useSurveyTemplate } from '@/hooks';
import { AppMessageSeverities, SurveyStatus } from '@/types';
import { Alert, Snackbar } from '@/ui';
import { track } from '@/utils';

import Viewer from '../Viewer';

import SurveyBuilder from './SurveyBuilder';
import Tabs from './Tabs';

import type { FC } from 'react';

// This view is only seen by client, not by coach
const Survey: FC = () => {
  const [activeTab, setActiveTab] = useState(ZERO);
  const [showActiveSurveyNotice, setShowActiveSurveyNotice] = useState(false);
  const [initialActiveSurveyId, setInitialActiveSurveyId] = useState<
    string | null | undefined
  >(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const { templateId = '' } = useParams();
  const isNewLaunchMode = location.pathname.endsWith('/new');

  const { isUserLoading } = useCurrentUser();
  const { activeSurvey, survey, isSurveyLoading } = useSurvey();
  const { surveyTemplate, isSurveyTemplateLoading } = useSurveyTemplate();

  const isLoading = isUserLoading || isSurveyLoading || isSurveyTemplateLoading;

  useEffect(() => {
    setInitialActiveSurveyId(isNewLaunchMode ? undefined : null);
  }, [isNewLaunchMode, templateId]);

  useEffect(() => {
    if (!isNewLaunchMode || isLoading || initialActiveSurveyId !== undefined) {
      return;
    }

    setInitialActiveSurveyId(activeSurvey?.id ?? null);
  }, [activeSurvey?.id, initialActiveSurveyId, isLoading, isNewLaunchMode]);

  useEffect(() => {
    const state = location.state as { blockedNewSurvey?: boolean } | null;

    if (!state?.blockedNewSurvey) {
      return;
    }

    setShowActiveSurveyNotice(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (
      !isNewLaunchMode ||
      !initialActiveSurveyId ||
      !activeSurvey?.id ||
      !templateId
    ) {
      return;
    }

    track('Clicked Survey In Progress', {
      surveyTemplate: surveyTemplate.name,
    });
    navigate(`/m/insights/${templateId}`, {
      replace: true,
      state: { blockedNewSurvey: true },
    });
  }, [
    activeSurvey?.id,
    initialActiveSurveyId,
    isNewLaunchMode,
    navigate,
    surveyTemplate.name,
    templateId,
  ]);

  if (isLoading || (isNewLaunchMode && initialActiveSurveyId === undefined)) {
    return null;
  }

  if (isNewLaunchMode && initialActiveSurveyId) {
    return null;
  }

  const isExample = activeTab === ONE;
  const surveyForBuilder = isNewLaunchMode ? ({} as typeof survey) : survey;

  const showViewer =
    !isNewLaunchMode &&
    (isExample ||
      survey.status === SurveyStatus.IN_PROGRESS ||
      survey.status === SurveyStatus.COACH_REVIEWING ||
      survey.status === SurveyStatus.SHARED);

  const showHeader = !showViewer || isExample;

  return (
    <>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={5000}
        open={showActiveSurveyNotice}
        onClose={() => setShowActiveSurveyNotice(false)}
      >
        <Alert
          severity={AppMessageSeverities.Info}
          style={{ width: '100%' }}
          onClose={() => setShowActiveSurveyNotice(false)}
        >
          You already have an active peer insights survey in progress. We
          redirected you to it.
        </Alert>
      </Snackbar>
      {showHeader && (
        <>
          <h1 className="headline m-0 text-cement-600">
            {surveyTemplate.name}
          </h1>
          <Tabs setTab={setActiveTab} tab={activeTab} />
        </>
      )}
      {showViewer ? (
        <div className="pt-4">
          <Viewer isExample={isExample} />
        </div>
      ) : (
        <SurveyBuilder survey={surveyForBuilder} template={surveyTemplate} />
      )}
    </>
  );
};

export default Survey;
